<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import {
  X,
  NotebookPen,
  Plus,
  Calendar,
  User,
  Search,
  Trash,
  Pencil,
  GripVertical,
  Copy,
} from 'lucide-vue-next'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import AppFormNotes from './AppFormNotes.vue'
import {
  useNotesService,
  type CreateNotePayload,
  type Note,
  type UpdateNotePayload,
} from '@/services/notes/useNotesService'
import { useToast } from 'primevue/usetoast'
const toast = useToast()
import { useI18n } from 'vue-i18n'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { useAuthStore } from '@/stores/useAuthStore'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import { formatDate } from '@/utils/formatDate.ts'
const { t } = useI18n()

interface Props {
  modelValue?: boolean
  embedded?: boolean
  title?: string
  initialX?: number
  initialY?: number
  initialWidth?: number
  initialHeight?: number
  zIndex?: number
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  embedded: false,
  title: 'notes.title',
  initialX: 250,
  initialY: 100,
  initialWidth: 550,
  initialHeight: 440,
  zIndex: 9999,
})
const emit = defineEmits(['update:modelValue', 'drag-start', 'drag-end', 'resize'])
const workspace = useWorkspaceStore()
const authStore = useAuthStore()
const notesService = useNotesService()

const notes = ref<Note[]>([])
const noteToEdit = ref<Note | null>(null)
const selectedNote = ref<Note | null>(null)
const draggedNoteId = ref<string | null>(null)
const search = ref('')
const showForm = ref(false)
const isLoadingNotes = ref(false)

const currentUserId = computed(() => (authStore.userId ? String(authStore.userId) : null))
const storageUserId = computed(() => currentUserId.value || 'anonymous')
const noteOrderStorageKey = computed(() => `oper_notes_order:${storageUserId.value}`)
const notePositionStorageKey = computed(() => `oper_notes_positions:${storageUserId.value}`)
const panelStateStorageKey = computed(() => `oper_notes_panel_state:${storageUserId.value}`)

type NotesPanelState = {
  x: number
  y: number
  width: number
  height: number
}

const sortedNotes = computed(() =>
  [...notes.value].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
)

const filteredNotes = computed(() =>
  search.value
    ? sortedNotes.value.filter((n) =>
        `${n.title} ${n.content}`.toLowerCase().includes(search.value.toLowerCase()),
      )
    : sortedNotes.value,
)

const getNoteCardStyle = (color?: string | null) => {
  const noteColor = color || '#3B82F6'
  return {
    borderColor: noteColor,
    backgroundColor: `color-mix(in srgb, ${noteColor} 14%, transparent)`,
  }
}

const openCreateForm = () => {
  noteToEdit.value = null
  showForm.value = true
}

const openEditForm = (note: Note) => {
  noteToEdit.value = note
  showForm.value = true
}

const closeForm = () => {
  showForm.value = false
  noteToEdit.value = null
}

const showErrorToast = (detail: string) => {
  toast.add({
    severity: 'error',
    summary: t('general.error'),
    detail,
    life: 3000,
  })
}

const withPosition = (items: Note[]) => items.map((note, index) => ({ ...note, position: index }))

const getStoredValue = (primaryKey: string, fallbackKeys: string[]) => {
  for (const key of [primaryKey, ...fallbackKeys]) {
    const value = localStorage.getItem(key)
    if (value) return value
  }

  return null
}

const readStoredNoteOrder = () => {
  try {
    const raw = getStoredValue(noteOrderStorageKey.value, [
      'oper_notes_order:anonymous',
      'oper_notes_order',
    ])
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

const readStoredNotePositions = () => {
  try {
    const raw = getStoredValue(notePositionStorageKey.value, [
      'oper_notes_positions:anonymous',
      'oper_notes_positions',
    ])
    const parsed = raw ? JSON.parse(raw) : {}
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return new Map<string, number>()

    return new Map(
      Object.entries(parsed)
        .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
        .sort((a, b) => a[1] - b[1]),
    )
  } catch {
    return new Map<string, number>()
  }
}

const saveStoredNoteOrder = (items: Note[]) => {
  const ids = items.map((note) => note.id)
  const positions = Object.fromEntries(items.map((note, index) => [note.id, index]))

  localStorage.setItem(noteOrderStorageKey.value, JSON.stringify(ids))
  localStorage.setItem(notePositionStorageKey.value, JSON.stringify(positions))
}

const applyStoredNoteOrder = (items: Note[]) => {
  const order = readStoredNoteOrder()
  const positions = readStoredNotePositions()
  if (!order.length && !positions.size) return withPosition(items)

  const orderIndex = positions.size ? positions : new Map(order.map((id, index) => [id, index]))
  const ordered = [...items].sort((a, b) => {
    const aIndex = orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER
    const bIndex = orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER

    if (aIndex !== bIndex) return aIndex - bIndex
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })

  return withPosition(ordered)
}

const loadNotes = async () => {
  try {
    isLoadingNotes.value = true
    // getAllNotes recorre todas las paginas: el orden por drag & drop y el buscador
    // trabajan en cliente sobre la lista completa.
    const {
      notes: allNotes,
      total,
      truncated,
    } = await notesService.getAllNotes({
      user_id: currentUserId.value || undefined,
      type: 'OPER',
    })
    notes.value = applyStoredNoteOrder(allNotes)
    saveStoredNoteOrder(notes.value)
    if (truncated) {
      toast.add({
        severity: 'warn',
        summary: t('general.warning'),
        detail: t('notes.partial_load_warning', { loaded: allNotes.length, total }),
        life: 5000,
      })
    }
  } catch {
    showErrorToast(t('notes.error_fetching_notes'))
  } finally {
    isLoadingNotes.value = false
  }
}

const saveNote = async (dto: CreateNotePayload) => {
  try {
    await notesService.createNote({
      ...dto,
      user_id: dto.user_id || currentUserId.value,
      color: dto.color || '#3B82F6',
      is_pinned: dto.is_pinned ?? false,
      type: 'OPER',
    })
    await loadNotes()
    toast.add({
      severity: 'success',
      summary: t('general.success'),
      detail: t('notes.success_saved_note'),
      life: 3000,
    })
    closeForm()
  } catch {
    showErrorToast(t('notes.error_saving_note'))
  }
}
const editNote = async (note: Note) => {
  try {
    const payload: UpdateNotePayload = {
      title: note.title,
      content: note.content,
      number: note.number,
      user_id: note.user_id || currentUserId.value,
      color: note.color || '#3B82F6',
      is_pinned: note.is_pinned,
      extension_id: note.extension_id,
      type: note.type || 'OPER',
    }

    await notesService.updateNote(note.id, payload)
    await loadNotes()
    if (selectedNote.value?.id === note.id) {
      selectedNote.value = notes.value.find((item) => item.id === note.id) || null
    }
    toast.add({
      severity: 'success',
      summary: t('general.success'),
      detail: t('notes.success_updated_note'),
      life: 3000,
    })
    closeForm()
  } catch {
    showErrorToast(t('notes.error_saving_note'))
  }
}
const noteToDelete = ref<Note | null>(null)

const deleteNote = async () => {
  if (!noteToDelete.value) return
  try {
    await notesService.deleteNote(noteToDelete.value.id)
    await loadNotes()
    if (selectedNote.value?.id === noteToDelete.value.id) {
      selectedNote.value = null
    }
    toast.add({
      severity: 'success',
      summary: t('general.success'),
      detail: t('notes.success_deleted_note'),
      life: 3000,
    })
    noteToDelete.value = null
  } catch {
    showErrorToast(t('notes.error_deleting_note'))
  }
}

const copySelectedNote = async () => {
  if (!selectedNote.value) return

  const documentFragment = new DOMParser().parseFromString(selectedNote.value.content, 'text/html')
  const content = (
    documentFragment.body.innerText ||
    documentFragment.body.textContent ||
    ''
  ).trim()
  if (!content) return

  try {
    await navigator.clipboard.writeText(content)
    toast.add({
      severity: 'success',
      summary: t('general.success'),
      detail: t('notes.success_copied_note'),
      life: 3000,
    })
  } catch {
    showErrorToast(t('notes.error_copying_note'))
  }
}

const persistNoteOrder = async (orderedNotes: Note[]) => {
  const updatedNotes = orderedNotes.map((note, index) => ({ ...note, position: index }))
  notes.value = updatedNotes
  saveStoredNoteOrder(updatedNotes)
}

const onNoteDragStart = (noteId: string, event: DragEvent) => {
  draggedNoteId.value = noteId
  event.dataTransfer?.setData('text/plain', noteId)
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

const onNoteDrop = async (targetNoteId: string) => {
  const sourceNoteId = draggedNoteId.value
  draggedNoteId.value = null
  if (!sourceNoteId || sourceNoteId === targetNoteId) return

  const visibleNotes = [...filteredNotes.value]
  const sourceIndex = visibleNotes.findIndex((note) => note.id === sourceNoteId)
  const targetIndex = visibleNotes.findIndex((note) => note.id === targetNoteId)
  if (sourceIndex === -1 || targetIndex === -1) return

  const [movedNote] = visibleNotes.splice(sourceIndex, 1)
  visibleNotes.splice(targetIndex, 0, movedNote)

  const visibleIds = new Set(visibleNotes.map((note) => note.id))
  let nextVisibleIndex = 0
  const reorderedNotes = sortedNotes.value.map((note) =>
    visibleIds.has(note.id) ? visibleNotes[nextVisibleIndex++] : note,
  )

  await persistNoteOrder(reorderedNotes)
}

const el = ref<HTMLElement | null>(null)
const dragging = ref(false)
const offsetX = ref(0)
const offsetY = ref(0)
const posX = ref(props.initialX)
const posY = ref(props.initialY)
const width = ref(props.initialWidth)
const height = ref(props.initialHeight)
const resizing = ref(false)
const startW = ref(0)
const startH = ref(0)
const startX = ref(0)
const startY = ref(0)

const clampPanelToViewport = () => {
  const maxX = Math.max(0, window.innerWidth - width.value)
  const maxY = Math.max(0, window.innerHeight - height.value)
  posX.value = Math.min(Math.max(0, posX.value), maxX)
  posY.value = Math.min(Math.max(0, posY.value), maxY)
}

const readStoredPanelState = (): NotesPanelState | null => {
  try {
    const raw = localStorage.getItem(panelStateStorageKey.value)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<NotesPanelState>
    if (
      typeof parsed.x !== 'number' ||
      typeof parsed.y !== 'number' ||
      typeof parsed.width !== 'number' ||
      typeof parsed.height !== 'number'
    ) {
      return null
    }

    return parsed as NotesPanelState
  } catch {
    return null
  }
}

const savePanelState = () => {
  if (props.embedded) return

  localStorage.setItem(
    panelStateStorageKey.value,
    JSON.stringify({
      x: posX.value,
      y: posY.value,
      width: width.value,
      height: height.value,
    }),
  )
}

const applyStoredPanelState = () => {
  const savedState = readStoredPanelState()
  if (!savedState) return

  posX.value = savedState.x
  posY.value = savedState.y
  width.value = Math.max(300, savedState.width)
  height.value = Math.max(200, savedState.height)
  clampPanelToViewport()
}

const startDrag = (e: MouseEvent) => {
  if (props.embedded) return
  dragging.value = true
  offsetX.value = e.clientX - posX.value
  offsetY.value = e.clientY - posY.value
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  emit('drag-start')
}
const onDrag = (e: MouseEvent) => {
  if (!dragging.value) return
  posX.value = Math.max(0, e.clientX - offsetX.value)
  posY.value = Math.max(0, e.clientY - offsetY.value)
  clampPanelToViewport()
}
const stopDrag = () => {
  dragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  savePanelState()
  emit('drag-end')
}

const startResize = (e: MouseEvent) => {
  if (props.embedded) return
  resizing.value = true
  startW.value = width.value
  startH.value = height.value
  startX.value = e.clientX
  startY.value = e.clientY
  document.addEventListener('mousemove', onResizeMove)
  document.addEventListener('mouseup', stopResize)
  e.preventDefault()
}
const onResizeMove = (e: MouseEvent) => {
  if (!resizing.value) return
  width.value = Math.max(300, startW.value + (e.clientX - startX.value))
  height.value = Math.max(200, startH.value + (e.clientY - startY.value))
  clampPanelToViewport()
  emit('resize', { width: width.value, height: height.value })
}
const stopResize = () => {
  resizing.value = false
  document.removeEventListener('mousemove', onResizeMove)
  document.removeEventListener('mouseup', stopResize)
  savePanelState()
}

onMounted(async () => {
  applyStoredPanelState()
  await loadNotes()
})

watch(currentUserId, async (userId, previousUserId) => {
  if (!userId || userId === previousUserId) return
  await loadNotes()
})
onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('mousemove', onResizeMove)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<template>
  <div
    v-if="modelValue || embedded"
    ref="el"
    class="flex flex-col"
    :class="
      embedded
        ? 'relative h-full w-full rounded-lg shadow-none overflow-hidden'
        : 'fixed rounded-xl shadow-2xl'
    "
    :style="{
      top: embedded ? undefined : posY + 'px',
      left: embedded ? undefined : posX + 'px',
      width: embedded ? undefined : width + 'px',
      height: embedded ? undefined : height + 'px',
      zIndex: embedded ? undefined : props.zIndex,
      resize: 'none',
      overflow: embedded ? 'hidden' : 'auto',
    }"
  >
    <div
      class="w-full px-4 py-3 border-b border-gray-100 dark:border-[var(--p-surface-700)] select-none flex items-center justify-between flex-shrink-0"
      :class="embedded ? '' : 'cursor-move'"
      @mousedown="startDrag"
    >
      <div class="flex items-center gap-3">
        <div
          class="p-2 rounded-lg bg-[var(--p-primary-color)]/10 dark:bg-[var(--p-primary-color)]/20"
        >
          <NotebookPen
            :size="18"
            :stroke-width="2"
            class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-300)]"
          />
        </div>
        <div>
          <span class="text-sm font-semibold text-gray-900 dark:text-white tracking-wide">{{
            $t('notes.title')
          }}</span>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ notes.length }} {{ $t('notes.title') }} • {{ $t('notes.drag_to_move') }}
          </p>
        </div>
      </div>
      <button
        v-if="!embedded"
        class="text-sm cursor-pointer"
        @click.stop="workspace.toggleNotesPanel()"
      >
        <X
          :size="20"
          :stroke-width="2"
          class="text-gray-700 dark:text-[var(--p-text-100)] hover:text-[var(--p-primary-color)] dark:hover:text-[var(--p-primary-300)]"
        />
      </button>
    </div>

    <div class="flex flex-1 min-h-0 flex-col space-y-2 overflow-auto px-4 py-2">
      <div class="mb-2">
        <AppInput v-model="search" :placeholder="$t('notes.search_placeholder')" size="small">
          <template #icon>
            <Search :size="14" class="text-[var(--p-primary-color)]"></Search>
          </template>
        </AppInput>
      </div>

      <div
        class="grid gap-3"
        :class="filteredNotes.length === 0 ? 'grid-cols-1' : 'grid-cols-1 lg:!grid-cols-2'"
      >
        <button
          class="w-full p-4 border-2 border-dashed border-[var(--p-primary-color)]/50 dark:border-[var(--p-primary-color)]/60 rounded-xl hover:border-[var(--p-primary-color)] dark:hover:border-[var(--p-primary-color)] hover:bg-[var(--p-primary-color)]/10 dark:hover:bg-[var(--p-primary-color)]/20 transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer"
          :disabled="isLoadingNotes"
          :class="isLoadingNotes ? 'opacity-60 cursor-wait' : ''"
          @click="openCreateForm"
        >
          <div
            class="p-2 rounded-lg bg-[var(--p-primary-color)]/10 dark:bg-[var(--p-primary-color)]/20 group-hover:bg-[var(--p-primary-color)]/20 transition-colors"
          >
            <Plus
              :size="16"
              class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-300)]"
            />
          </div>
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
            $t('notes.add_note')
          }}</span>
        </button>

        <div
          v-for="note in filteredNotes"
          :key="note.id"
          draggable="true"
          :class="[
            'p-4 rounded-xl transition-all shadow-xs hover:shadow-md cursor-pointer',
            draggedNoteId === note.id ? 'opacity-50 ring-2 ring-[var(--p-primary-color)]' : '',
          ]"
          :style="getNoteCardStyle(note.color)"
          @click="selectedNote = note"
          @dragstart="onNoteDragStart(note.id, $event)"
          @dragend="draggedNoteId = null"
          @dragover.prevent
          @drop.prevent="onNoteDrop(note.id)"
        >
          <div class="flex items-start justify-between mb-3 gap-2">
            <GripVertical
              :size="18"
              class="mt-1 shrink-0 text-gray-800 dark:text-gray-200"
              aria-hidden="true"
            />

            <div class="w-full flex justify-between items-center">
              <h3 class="font-semibold text-gray-900 dark:text-gray-200 truncate">
                {{ note.title }}
              </h3>
              <div>
                <button
                  class="p-2 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded-lg transition-colors cursor-pointer"
                  aria-label="Edit note"
                  @click.stop="openEditForm(note)"
                >
                  <Pencil
                    :size="16"
                    class="text-[var(--p-primary-500)] hover:text-[var(--p-primary-600)] dark:hover:text-[var(--p-primary-400)]"
                  />
                </button>
                <button
                  class="p-2 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded-lg transition-colors cursor-pointer"
                  aria-label="Delete note"
                  @click.stop="noteToDelete = note"
                >
                  <Trash
                    :size="16"
                    class="text-red-400 hover:text-red-500 dark:hover:text-red-400"
                  />
                </button>
              </div>
            </div>
          </div>
          <p
            class="text-xs text-gray-800 dark:text-gray-300 mb-3 line-clamp-2"
            v-html="note.content"
          ></p>
        </div>
      </div>

      <div v-if="isLoadingNotes" class="flex justify-center py-8 text-[var(--p-primary-color)]">
        <i class="pi pi-spin pi-spinner text-xl" />
      </div>

      <div
        v-else-if="filteredNotes.length === 0"
        class="flex flex-1 flex-col items-center justify-center text-center"
      >
        <NotebookPen :size="48" class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p class="text-gray-500 dark:text-gray-400 font-medium">{{ $t('notes.no_notes_found') }}</p>
        <p class="text-sm text-gray-400 dark:text-gray-500 mt-1">
          {{ $t('notes.try_different_search_or_create') }}
        </p>
      </div>
    </div>

    <AppDialog
      v-model="showForm"
      :header="noteToEdit ? t('notes.edit_note') : t('notes.add_note')"
      width="min(92vw, 800px)"
      custom-class="backdrop-blur-xl!"
      :draggable="false"
      @update:modelValue="(value) => !value && closeForm()"
    >
      <template #header>
        <label class="font-medium text-md">
          {{ noteToEdit ? t('notes.edit_note') : t('notes.add_note') }}
        </label>
      </template>
      <template #content>
        <AppFormNotes
          :showForm="showForm"
          :infoNote="noteToEdit"
          @closeForm="closeForm"
          @saveNote="saveNote"
          @editNote="editNote"
        />
      </template>
    </AppDialog>

    <AppDialog
      :model-value="Boolean(selectedNote)"
      :header="selectedNote?.title || t('notes.title')"
      custom-class="backdrop-blur-xl!"
      width="min(92vw, 800px)"
      :draggable="false"
      @update:modelValue="(value) => !value && (selectedNote = null)"
    >
      <template #content>
        <div v-if="selectedNote" class="space-y-4">
          <div
            class="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400"
          >
            <div class="flex items-center gap-3">
              <span
                class="h-5 w-5 rounded-md border border-gray-300 dark:border-gray-600"
                :style="{ backgroundColor: selectedNote.color || '#3B82F6' }"
              />
              <span class="flex items-center gap-1">
                <Calendar :size="13" />{{ formatDate(selectedNote.created_at) }}
              </span>
            </div>
            <button
              type="button"
              class="inline-flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-200 hover:bg-[var(--p-primary-100)] dark:hover:bg-[var(--p-primary-900)] dark:hover:text-[var(--p-primary-400)]"
              :aria-label="t('notes.copy_note')"
              :title="t('notes.copy_note')"
              :disabled="!selectedNote.content"
              @click="copySelectedNote"
            >
              <Copy :size="15" />
              <!-- <span>{{ t('notes.copy_note') }}</span> -->
            </button>
          </div>
          <div
            class="ql-editor note-content text-sm leading-6 text-gray-700 dark:text-gray-200"
            v-html="selectedNote.content"
          />
        </div>
      </template>
    </AppDialog>

    <div
      v-if="!embedded"
      class="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize "
      @mousedown="startResize"
    >
      <svg class="w-3 h-3 text-gray-400 dark:text-gray-500" viewBox="0 0 12 12">
        <path fill="currentColor" d="M12 0L12 12L0 12Z" />
      </svg>
    </div>

    <div
      v-if="noteToDelete"
      class="fixed inset-0 z-[20000] flex items-center justify-center"
      @click.self="noteToDelete = null"
    >
      <div
        class="!bg-[var(--p-surface-100)]/90 dark:!bg-[var(--p-surface-900)]/30 backdrop-blur-xl! rounded-xl shadow-2xl p-6 w-11/12 max-w-sm border border-gray-100 dark:border-[var(--p-primary-400)]/20"
      >
        <div class="flex items-center gap-3">
          <Trash :size="25" />
          <span class="text-lg font-semibold text-gray-900 dark:text-white">{{
            t('notes.delete_title')
          }}</span>
        </div>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {{ t('notes.delete_message') }} “{{ noteToDelete.title }}”?
        </p>
        <div class="mt-6 flex justify-end gap-2">
          <AppButton
            variant="secondary"
            :label="t('general.cancel')"
            @click="noteToDelete = null"
          />
          <AppButton variant="danger" :label="t('general.delete')" @click="deleteNote" />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.note-content ul {
  list-style-type: disc;
  padding-left: 1.5rem;
}

.note-content ol {
  list-style-type: decimal;
  padding-left: 1.5rem;
}

.note-content li {
  margin: 0.25rem 0;
}

.note-content p {
  margin: 0.5rem 0;
}

.note-content blockquote {
  border-left: 4px solid #d1d5db;
  padding-left: 1rem;
  color: #6b7280;
}
</style>
